/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { InterPoint, localize, Logger } from '@saviynt/common';
import {
  Box,
  Checkbox,
  Chip,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import { httpService } from '../../../http-service/httpService';
import {
  setConnectedTileId,
  setIsSidePanelVisible,
  setSidePanelMetaData,
} from '../../../redux/certlist/action';
import { URL_SIDE_PANEL_PROGRESS_API } from '../../../redux/certlist/urls';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_LARGE,
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import { LinearProgressBar } from '../../Common';
import CertsListAction from '../CertsListAction';
import { ACTION_BUTTON_FOOTER_KIND, CERTS_LIST_TODO_TAB } from '../constant';

import {
  CAMP_TYPE_AO,
  CAMP_TYPE_EO,
  CAMP_TYPE_SA,
  CAMP_TYPE_UM,
  EXPIRING_SOON,
  TOOLTIP_DELAY_IN_MS_TILE,
} from './constant';
import { CERT_TILE_MOCK_DATA } from './mockData';
import {
  getAlertIconColor,
  getIconKind,
  getStatusIcon,
  getTileMetaData,
  getTileTabCategory,
} from './utils';

import './CertsListTile.css';

const SCROLL_THRESHOLD = 10;

/**
 * CertsListTile Component
 *
 * @description
 * CertsListTile renders a dynamic certification list tile with various attributes and interactive
 * controls. It supports features like role display, certification progress, due date alerts,
 * bulk action handling, and touch device interactions.
 *
 * The tile comprises a control box, a fixed column for the title, attributes for certification
 * metadata, and a status section. Depending on the application state, certain features and
 * styles are toggled, including bulk action checkboxes, tooltips, and progress bars.
 *
 * @component
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.certData - Certification data used for rendering the tile.
 *
 * @example
 * <CertsListTile certData={certificationData} />
 *
 * @dependencies
 * - `useSelector`: Used to extract state from the Redux store for the certification list.
 * - `PropTypes`: Used for validating the shape of `certData`.
 * - External components:
 *    -  Design System components
 *    - `LinearProgressBar`: Progress bar for certification progress.
 *    - `CertsListAction`: Component for tile-specific actions.
 * - Utilities:
 *    - `classnames`: Dynamically applies conditional class names.
 *    - `moment`: Formats dates for the certification due date.
 *
 * @features
 * - Dynamically renders tile components based on `certData` and global state.
 * - Supports interactive actions, including tile click, keypress, and bulk action toggling.
 * - Provides responsive styles for mobile and desktop views.
 * - Includes tooltips, progress bars, and status indicators for a comprehensive UI.
 * - Touch-enabled controls for mobile devices.
 * - Inlcludes click based sidepanel opening.
 *
 * @propTypes
 * - `certData` (Object): Certification data with attributes like `campaigntype`,
 *
 * @defaultProps
 * - `certData`: Empty object.
 */
function CertsListTile({ tileRef, certData }) {
  const dispatch = useDispatch();
  const {
    global: { mediaQueryValue, globalDateFormat, isWithoutCampaignKey },
    core: { isAdminLogin },
    bulkAction: { isBulkActionEnabled },
    sidePanel: { isSidePanelVisible, connectedTileId },
  } = useSelector((state) => state?.certifications?.list);
  const { isMenuPinned } = useSelector((state) => state?.user);

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user) || {};

  // Responsive selector queries
  const tabletLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_LARGE
  );
  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isTabletLargeWidth = tabletLargeQuery.value <= mediaQueryValue;
  const isTabletWidth = tabletSmallQuery.value <= mediaQueryValue;
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  // Tile configuration
  const [tiledata, setTileData] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const [isCheckboxToggling, setIsCheckboxToggling] = useState(false);
  // Touch point handling
  const [isTouchScrolling, setIsTouchScrolling] = useState(false);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });

  const [progress, setProgress] = useState('0');
  const [progressLoader, setProgressLoader] = useState(false);

  const campaignType = certData?.campaigntype;
  const validCampaignTypes =
    campaignType === CAMP_TYPE_UM ||
    campaignType === CAMP_TYPE_AO ||
    campaignType === CAMP_TYPE_SA ||
    campaignType === CAMP_TYPE_EO;

  const getProgressDetails = async () => {
    const { certkey } = certData;

    if (certkey) {
      const decisionBody = {
        certkey: String(certkey),
        group_by: 'decision',
      };

      try {
        setProgressLoader(true);
        const response = await httpService.post(
          URL_SIDE_PANEL_PROGRESS_API,
          decisionBody
        );

        if (response?.data) {
          const { Summary } = response.data;

          if (Summary?.Progress) setProgress(`${Summary.Progress}%`);
        }
      } catch (e) {
        Logger.error('getProgressDetails Error', e);
      } finally {
        setProgressLoader(false);
      }
    }
  };

  useEffect(() => {
    setTileData(certData);
  }, [certData]);

  // add this implementation only when login user is not an admin
  useEffect(() => {
    if (!isAdminLogin && validCampaignTypes) getProgressDetails();
  }, []);

  const isTileChecked = Boolean(tiledata?.tracker?.isChecked || isChecked);
  const shouldShowActionButtons = Boolean(
    !isSidePanelVisible || (isMenuPinned && isTabletLargeWidth) || isTabletWidth
  );

  const tileClasses = classnames(
    'CertsListTile',
    tiledata?.tracker?.isTransition && 'CertsListTile--isTransition',
    !isTabletWidth &&
      !(isMenuPinned && isTabletLargeWidth) &&
      tiledata?.tracker?.id === connectedTileId &&
      'CertsListTile--isConnected'
  );
  const tileOverlayBgClasses = classnames(
    'CertsListTile-backgroundOverlay',
    tileRef &&
      tileRef.current &&
      tileRef.current instanceof HTMLElement &&
      'CertsListTile-backgroundOverlay--isHighlighted'
  );
  const controlBoxClasses = classnames(
    'CertsListTile-controlBox',
    isTileChecked && 'CertsListTile-controlBox--isChecked'
  );
  const infoAreaClasses = classnames(
    'CertsListTile-infoArea',
    isAdminLogin && 'CertsListTile-infoArea--isAdminLogin',
    isSidePanelVisible && 'CertsListTile-infoArea--isSidePanelVisible'
  );

  // Validations here if needed in sidepanel should be added to function `getTileMetaData` also
  const tileStatusLabel = tiledata?.statusLabel;
  const tileTabLabel = getTileTabCategory(tileStatusLabel);
  const isPrimaryCertifierActive = Boolean(tiledata?.certifierStatus === 1);
  const isDueDateAlertAvailable = Boolean(
    tiledata?.autoRevoke && !isAdminLogin
  );
  const isExpireSoonAvailable = Boolean(
    tiledata?.dueDateLabel?.toLowerCase() === EXPIRING_SOON && isAdminLogin
  );

  // Handling tile click events here
  const handleTileCheck = () => {
    setIsChecked((prev) => !prev);
  };

  const handleTileClick = () => {
    setIsCheckboxToggling(false); // This has to removed later when bulk action is handled

    // Handle tile click to open sidepanel
    if (connectedTileId !== tiledata?.tracker?.id) {
      dispatch(setConnectedTileId(tiledata?.tracker?.id));
      dispatch(
        setSidePanelMetaData(
          getTileMetaData(messages, intl, tiledata, isAdminLogin)
        )
      );
    }

    if (!isSidePanelVisible) {
      dispatch(setIsSidePanelVisible(true));
    }
  };

  const handleKeyDownTile = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTileClick();
    }
  };

  /**
   * Renders a fixed column tile component with a campaign type label and certification name.
   *
   * This component uses `Box` and `Typography` for layout and text styling,
   * and includes a `Tooltip` for displaying additional information when the certification name is truncated.
   *
   * @function renderTileTitle
   * @returns {JSX.Element} A fixed column tile component.
   *
   * @dependencies
   * - `tiledata` (Object): The data object containing information for rendering the tile.
   *   - `campaigntype` (string): The type of the campaign used to determine the label.
   *   - `certificationName` (string): The name of the certification displayed in the column.
   * - `CAMP_TYPE_NA` (string): A default campaign type used when `tiledata.campaigntype` is undefined.
   * - `TOOLTIP_DELAY_IN_MS_TILE` (number): The delay in milliseconds before the tooltip is displayed.
   * - `MIN_LENGTH_FOR_COL_TRUNCATION_TITLE` (number): The minimum length of the certification name for truncation.
   *
   * @description
   * - Displays the certification name (`tiledata.certificationName`) with truncation if it exceeds a specified length.
   * - A tooltip is provided to show the full certification name when truncated.
   */
  const renderTileTitle = () => {
    const { kind: statusIconKind, iconColor: statusIconColor } = getStatusIcon(
      messages,
      intl,
      tileTabLabel,
      tileStatusLabel,
      isAdminLogin
    );
    const isCampaignType = Boolean(!isAdminLogin || isWithoutCampaignKey);

    return (
      <>
        {/* This title will be rendered in mobile mode through css */}
        <div className='CertsListTile-fixedColumnStatus--isTabletMode'>
          <Icon kind={statusIconKind} size='xSmall' color={statusIconColor} />
          <Typography
            kind='body3-bold'
            desktop='body3-bold-d'
            mobile='body3-bold-d'
            color='--color-foreground-neutral-bold'>
            {progressLoader ? (
              <Loader kind='dots' format='inline' color='brand' size='medium' />
            ) : (
              (() => {
                if (tiledata?.progress && !validCampaignTypes) {
                  return tiledata?.progress;
                }

                return progress || tiledata?.progress;
              })()
            )}
          </Typography>
          <InterPoint />

          {isPrimaryCertifierActive ? (
            <Typography
              kind='body3'
              desktop='body3-d'
              mobile='body3-d'
              color='--color-foreground-secondary-bold'
              tooltipConfigs={{
                maxWidth: '20rem',
                openDelay: TOOLTIP_DELAY_IN_MS_TILE,
              }}
              lineLimit={1}>
              {`${
                isCampaignType ? tiledata?.campaigntype : tiledata?.certifier
              }`}
            </Typography>
          ) : (
            <Chip
              kind='subtleFilled'
              label={
                messages.CERTS_LIST_TILE_INACTIVE ||
                localize(intl, getMessageKey('CERTS_LIST_TILE_INACTIVE'))
              }
              color='warning'
              size='xSmall'
              tabIndex={-1}
            />
          )}
        </div>

        {/* This title will be rendered in dekstop mode through css */}
        {isCampaignType && (
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            className='CertsListTile-fixedColumnStatusCampType'
            color='--color-foreground-secondary-bold'>
            {tiledata?.campaigntype || ' '}
          </Typography>
        )}
        <Typography
          kind='h3'
          desktop='h3-d'
          mobile='h3-d'
          color='--color-foreground-neutral-bold'
          tooltipConfigs={{
            maxWidth: '20rem',
            openDelay: TOOLTIP_DELAY_IN_MS_TILE,
          }}
          lineLimit={1}>
          {tiledata?.certificationName || ' '}
        </Typography>
      </>
    );
  };

  /**
   * Renders a set of tile attributes including role, certifier status, and due date.
   *
   * This function returns a collection of UI elements using the Box and Typography components
   * to display specific information related to certification attributes. It also conditionally
   * renders additional components such as a Chip for inactive certifiers or a Tooltip for due
   * date alerts.
   *
   * Components:
   * 1. **Role Display**:
   *    - Displays the "My Role" label and a attribute value.
   *
   * 2. **Primary Certifier**:
   *    - Conditionally rendered when `isSidePanelVisible` is false.
   *    - Displays the "Primary Certifier" label and either:
   *      - The active certifier name (`tiledata?.certifier`), or
   *      - An "Inactive" Chip if `isPrimaryCertifierActive` is false.
   *
   * 3. **Due Date**:
   *    - Displays the "Due Date" label.
   *    - If `isDueDateAlertAvailable` is true:
   *        - Adds an alert icon with a tooltip explaining due date importance.
   *    - Displays the formatted end date retrieved from `tiledata?.enddate`.
   *
   * Props and Dependencies:
   * - `isSidePanelVisible` (boolean): Controls visibility of the "Primary Certifier" section.
   * - `isPrimaryCertifierActive` (boolean): Determines if the certifier is active.
   * - `isDueDateAlertAvailable` (boolean): Adds an alert for the due date if true.
   * - `tiledata` (object): Contains the data to display for attributes, like certifier name and end date.
   * - `globalDateFormat` (string): Date format to display the end date.
   * - `TOOLTIP_DELAY_IN_MS_TILE` (constant): Tooltip delay configuration.
   *
   * Dependencies:
   * - React components: `Box`, `Typography`, `Chip`, `Tooltip`, `Icon`
   * - External libraries: `moment` for date formatting.
   *
   * Returns:
   * - A React fragment containing the rendered UI elements for tile attributes.
   */
  const renderTileAttributes = () => (
    <>
      {/* My Role is not currently supported */}
      {/* <Box tag='div' className='CertsListTile-attribute'>
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          color='--color-foreground-neutral-subtle'>
          My Role
        </Typography>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color='--color-foreground-secondary-bold'
          tooltipConfigs={{
            maxWidth: '20rem',
            openDelay: TOOLTIP_DELAY_IN_MS_TILE,
          }}
          lineLimit={1}>
          {isAdminLogin ? 'Admin, Campaign Owner' : 'WIP: My Role'}
        </Typography>
      </Box> */}
      {(!isSidePanelVisible || isAdminLogin) && (
        <Box tag='div' className='CertsListTile-attribute'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color='--color-foreground-neutral-medium'>
            {messages.CERTS_LIST_PRIMARY_CERTIFIER ||
              localize(intl, getMessageKey('CERTS_LIST_PRIMARY_CERTIFIER'))}
          </Typography>

          {isPrimaryCertifierActive ? (
            <Typography
              kind='body2'
              desktop='body2-d'
              mobile='body2-d'
              color='--color-foreground-secondary-bold'
              tooltipConfigs={{
                maxWidth: '20rem',
                openDelay: TOOLTIP_DELAY_IN_MS_TILE,
              }}
              lineLimit={1}>
              {`${tiledata?.certifier}`}
            </Typography>
          ) : (
            <Tooltip
              trigger={
                <Chip
                  kind='subtleFilled'
                  label={
                    messages.CERTS_LIST_TILE_INACTIVE ||
                    localize(intl, getMessageKey('CERTS_LIST_TILE_INACTIVE'))
                  }
                  color='warning'
                  size='small'
                  tabIndex={-1}
                />
              }
              isPortalEnabled
              maxWidth='12rem'
              openDelay={TOOLTIP_DELAY_IN_MS_TILE}
              text='This certification is no longer assigned to an active certifier.'
              placement='bottom'
            />
          )}
        </Box>
      )}
      {!isAdminLogin && (
        <Box tag='div' className='CertsListTile-attribute'>
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            className={
              isDueDateAlertAvailable ? 'CertsListTile-attribute--isDue' : ''
            }
            color='--color-foreground-neutral-medium'>
            {isDueDateAlertAvailable ? (
              <>
                {`Due Date `}
                <Tooltip
                  openDelay={TOOLTIP_DELAY_IN_MS_TILE}
                  text='If the certification is not submitted by the due date, some access will be revoked automatically.'
                  placement='right-start'
                  trigger={
                    <Icon
                      kind='AlertWarningFilledSeparateColors'
                      size='xSmall'
                    />
                  }
                />
              </>
            ) : (
              'Due Date'
            )}
          </Typography>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-secondary-bold'
            tooltipConfigs={{
              maxWidth: '20rem',
              openDelay: TOOLTIP_DELAY_IN_MS_TILE,
            }}
            lineLimit={1}>
            {moment
              .utc(tiledata?.enddate)
              .local()
              .format(globalDateFormat?.toUpperCase())}
          </Typography>
        </Box>
      )}
    </>
  );

  // Touch device for checkbox actions
  let animationFrameId;

  /**
   * Handles the pointer down event for touch devices to initiate touch tracking.
   * Sets the initial touch position and resets the scrolling state.
   *
   * @param {PointerEvent} e - The pointer event triggered by the user's touch action.
   */
  const handlePointerDown = (e) => {
    if (e.pointerType === 'touch' && isBulkActionEnabled) {
      setTouchStartPosition({ x: e.clientX, y: e.clientY });
      setIsTouchScrolling(false);
    }
  };

  /**
   * Handles the pointer move event for touch devices to detect scrolling.
   * Uses requestAnimationFrame to efficiently track touch movement and determine if scrolling occurs.
   *
   * @param {PointerEvent} e - The pointer event triggered by the user's touch movement.
   */
  const handlePointerMove = (e) => {
    if (animationFrameId || e.pointerType !== 'touch' || !isBulkActionEnabled)
      return;

    animationFrameId = requestAnimationFrame(() => {
      const deltaX = Math.abs(e.clientX - touchStartPosition.x);
      const deltaY = Math.abs(e.clientY - touchStartPosition.y);

      if (deltaX > SCROLL_THRESHOLD || deltaY > SCROLL_THRESHOLD) {
        setIsTouchScrolling(true);
      }

      animationFrameId = null;
    });
  };

  /**
   * Handles the pointer up event for touch devices to toggle the checkbox state.
   * Checks if the touch action was not a scroll and toggles the checkbox if applicable.
   *
   * @param {PointerEvent} e - The pointer event triggered by the user's touch release action.
   */
  const handlePointeUp = (e) => {
    if (
      e.pointerType === 'touch' &&
      isBulkActionEnabled &&
      !isTouchScrolling &&
      !isCheckboxToggling
    ) {
      handleTileCheck();
    }
  };

  /**
   * Renders the control box for a tile, either as an icon-only version or a full version with a checkbox,
   * depending on the `isBulkActionEnabled` flag.
   *
   * @function renderTileControlBox
   * @returns {JSX.Element} The control box component for the tile.
   *
   * @dependencies
   * - `isBulkActionEnabled` (boolean): Determines whether the bulk action is enabled.
   * - `tiledata` (Object): The data object containing information for rendering the tile.
   *   - `campaigntype` (string): The type of the campaign used to determine the icon kind.
   * - `getIconKind` (Function): A function that returns the icon type based on the campaign type.
   * - `isMobileXlargeWidth` (boolean): A flag indicating whether the screen width is at a mobile x-large breakpoint.
   * - `isTileChecked` (boolean): Indicates whether the checkbox is checked.
   * - `controlBoxClasses` (string): CSS class names for the control box element.
   * - `handlePointerDown` (Function): Callback for the `onPointerDown` event on the control box.
   * - `handlePointerMove` (Function): Callback for the `onPointerMove` event on the control box.
   * - `handlePointeUp` (Function): Callback for the `onPointerUp` event on the control box.
   * - `handleTileCheck` (Function): Callback for handling checkbox state changes.
   *
   * @description
   * - If `isBulkActionEnabled` is `false`, renders an icon-only version of the control box.
   * - If `isBulkActionEnabled` is `true`, renders a full control box that includes:
   *   - An icon determined by the campaign type.
   *   - A `Checkbox` component with hover effects, tied to tile selection state.
   * - Includes pointer event handlers for additional interaction in the bulk action mode.
   */
  const renderTileControlBox = () => {
    if (!isBulkActionEnabled) {
      return (
        <div
          tabIndex='0'
          role='button'
          aria-label='Open side panel'
          onClick={handleTileClick}
          onKeyDown={(e) => handleKeyDownTile(e)}
          className='CertsListTile-controlBox--isIconOnly'>
          <Icon
            color={
              isPrimaryCertifierActive
                ? 'navy-900'
                : getAlertIconColor(tileStatusLabel)
            }
            className='CertsListTile-controlBoxIcon'
            kind={
              isPrimaryCertifierActive
                ? getIconKind(tiledata?.campaigntype)
                : 'AlertCriticalFilled'
            }
            size={
              isMobileXlargeWidth || !isPrimaryCertifierActive
                ? 'medium'
                : 'smallMedium'
            }
          />
        </div>
      );
    }

    return (
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointeUp}
        className={controlBoxClasses}>
        <Icon
          color={isPrimaryCertifierActive ? 'navy-900' : 'warning-500'}
          className='CertsListTile-controlBoxIcon'
          kind={
            isPrimaryCertifierActive
              ? getIconKind(tiledata?.campaigntype)
              : 'AlertCritical'
          }
          size={
            isMobileXlargeWidth || !isPrimaryCertifierActive
              ? 'medium'
              : 'smallMedium'
          }
        />
        <Checkbox
          id='certs-list-tile-checkbox'
          name='checkbox-no-hovereffect'
          showHoverEffect
          isChecked={isTileChecked}
          onChange={handleTileCheck}
          dataTestId='checkbox-no-hovereffect'
        />
      </div>
    );
  };

  /**
   * Renders the tile status based on various conditions.
   *
   * This function dynamically generates and returns UI elements to represent
   * the status of a tile. The rendered content depends on several factors such as
   * user role, tile progress, and specific alerts.
   *
   * @function renderTileControlBox
   * @returns {JSX.Element} - The JSX representing the tile status.
   *
   * Logic:
   * - If the user is an admin and a due date alert is available:
   *   - Renders a warning `Chip` component with the label "Expire Soon".
   * - If the user is an admin:
   *   - Renders an `Icon` component for the status and a `Typography` component
   *     for status text.
   * - For non-admin users:
   *   - Renders an `Icon` for the status, a `Typography` displaying either the
   *     status text or tile progress, and optionally a `LinearProgressBar`
   *     if the side panel is not visible.
   *
   * Dependencies:
   * - `tiledata` (object): Contains data related to the tile, including progress.
   * - `activeTab` (string): Used to fetch the status icon and related details.
   * - `isAdminLogin` (boolean): Determines if the user is an admin.
   * - `isDueDateAlertAvailable` (boolean): Indicates if a due date alert is present.
   * - `isSidePanelVisible` (boolean): Controls the visibility of the progress bar.
   *
   * Helper Function:
   * - `getStatusIcon(activeTab)`: Retrieves icon details (kind, color, and text)
   *   based on the currently active tab.
   */
  const renderTileStatus = () => {
    const progressValue =
      Number(progress?.split('%')?.[0]) > 0
        ? Number(progress?.split('%')?.[0])
        : Number(tiledata?.progress?.split('%')?.[0]) || 0;
    const {
      kind: statusIconKind,
      iconColor: statusIconColor,
      text: statusText,
      textColor: statusTextColor,
    } = getStatusIcon(
      messages,
      intl,
      tileTabLabel,
      tileStatusLabel,
      isAdminLogin
    );

    if (isExpireSoonAvailable) {
      return (
        <Chip
          kind='outlined'
          label={
            messages?.CERTS_LIST_TILE_EXPIRE_SOON ||
            localize(intl, getMessageKey('CERTS_LIST_TILE_EXPIRE_SOON'))
          }
          color='warning'
          size='small'
          tabIndex={-1}
        />
      );
    }

    if (isAdminLogin) {
      return (
        <>
          <Icon kind={statusIconKind} size='large' color={statusIconColor} />
          <Typography
            kind='body3'
            desktop='body3-d'
            mobile='body3-d'
            color={statusTextColor}>
            {statusText}
          </Typography>
        </>
      );
    }

    const isReadyToSubmit = Boolean(
      !isSidePanelVisible &&
        tileTabLabel === CERTS_LIST_TODO_TAB &&
        tiledata?.progress === '100%' &&
        false // Keeping false as discussion pending from PO
    );

    return (
      <>
        <Icon kind={statusIconKind} size='large' color={statusIconColor} />
        <Typography
          kind='body2-bold'
          desktop='body2-bold-d'
          mobile='body2-bold-d'
          color={statusTextColor}>
          {progressLoader ? (
            <Loader kind='dots' format='inline' color='brand' size='medium' />
          ) : (
            (() => {
              if (tiledata?.progress && !validCampaignTypes) {
                return tiledata?.progress;
              }

              return progress || tiledata?.progress;
            })()
          )}
        </Typography>
        {isReadyToSubmit && (
          <Tooltip
            maxWidth='10.5rem'
            placement='right-start'
            isPortalEnabled
            trigger={<Icon kind='Info' size='xsmall' color='info-700' />}
            text='Waiting for the primary certifier to submit the certification.'
          />
        )}
        {!isSidePanelVisible && <LinearProgressBar value={progressValue} />}
      </>
    );
  };

  return (
    <div className={tileClasses} key={tiledata?.tracker?.id} ref={tileRef}>
      {renderTileControlBox()}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={handleTileClick} className={infoAreaClasses}>
        <Box tag='div' className='CertsListTile-fixedColumn'>
          {renderTileTitle()}
        </Box>
        <Box tag='div' className='CertsListTile-attributeBox' tabIndex={0}>
          {renderTileAttributes()}
        </Box>
        <Box tag='div' className='CertsListTile-statusBox'>
          {renderTileStatus()}
        </Box>
      </div>
      {/* Prop passed for below action component need to be pass in object in `getTileMetaData` unless the prop is not required in sidepanel */}
      {shouldShowActionButtons && (
        <CertsListAction
          kind={ACTION_BUTTON_FOOTER_KIND.tile}
          certKey={tiledata?.certkey}
          actionsMap={tiledata?.tracker?.actions}
          isPrimaryCertifierActive={isPrimaryCertifierActive}
          tileCampaignKey={tiledata?.campaignKey}
          campaignType={tiledata?.campaigntype}
          tileStatusLabel={tileStatusLabel}
          newCertUX={tiledata?.newCertUX}
          certDetailsUrl={tiledata?.certDetailsUrl}
          handleTileClick={handleTileClick}
          certificationName={tiledata?.certificationName}
          tileData={tiledata}
        />
      )}
      <span className='CertsListTile-overlay' />
      <span className={tileOverlayBgClasses} />
    </div>
  );
}

CertsListTile.propTypes = {
  tileRef: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }),
  certData: PropTypes.shape(CERT_TILE_MOCK_DATA),
};

CertsListTile.defaultProps = {
  tileRef: null,
  certData: {},
};

export default React.memo(CertsListTile);
